import { Box, Typography } from '@mui/material';
import React, { useId, useState } from 'react';

interface LibraryVideoPlayerProps {
  url: string;
  /** The title to show: the card's own title when it has one, else the video's user-facing title. */
  title?: string;
  description?: string;
  videoStyle?: React.CSSProperties;
}

const OVERLAY_SX = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  right: 0,
  px: 2,
  pt: 1.5,
  pb: 4,
  color: '#fff',
  background: 'linear-gradient(rgba(0,0,0,0.75), rgba(0,0,0,0))',
  borderTopLeftRadius: 'inherit',
  borderTopRightRadius: 'inherit',
  pointerEvents: 'none' as const,
  transition: 'opacity 0.2s ease',
};

const clamp = (lines: number) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
});

/**
 * A library video's player with a YouTube-style caption: its title and
 * description sit over the top of the frame whenever the video isn't playing
 * (before play, paused, ended) and while the pointer or keyboard focus is on
 * it, then fade away during playback so they never cover the picture.
 */
export default function LibraryVideoPlayer({
  url,
  title,
  description,
  videoStyle,
}: LibraryVideoPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [engaged, setEngaged] = useState(false);
  const descriptionId = useId();
  const hasCaption = !!(title || description);

  return (
    <Box
      sx={{ position: 'relative', width: '100%', height: '100%', borderRadius: 'inherit' }}
      onMouseEnter={() => setEngaged(true)}
      onMouseLeave={() => setEngaged(false)}
      onFocusCapture={() => setEngaged(true)}
      onBlurCapture={() => setEngaged(false)}
    >
      <video
        controls
        preload="metadata"
        src={url}
        aria-label={title}
        aria-describedby={description ? descriptionId : undefined}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        style={{ width: '100%', height: '100%', display: 'block', ...videoStyle }}
      />
      {hasCaption && (
        <Box
          data-testid="video-caption"
          aria-hidden
          sx={{ ...OVERLAY_SX, opacity: engaged || !playing ? 1 : 0 }}
        >
          {title && (
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3, ...clamp(1) }}>
              {title}
            </Typography>
          )}
          {description && (
            <Typography
              id={descriptionId}
              variant="body2"
              sx={{ mt: 0.25, opacity: 0.9, ...clamp(2) }}
            >
              {description}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
